/**
 * Polkadot block 由来 action の保存と fiscal lock 退避を担当する。
 */
import type { Action } from '@0xtorch/core'
import { divideArrayIntoChunks } from '@pkg/basic'
import { createPolkadotAddress, createPolkadotSource } from '@pkg/polkadot'
import { and, eq, inArray, sql } from 'drizzle-orm'
import {
  actionSourceColumnCount,
  sqliteMaxHostParameterCount,
} from '../../constants'
import {
  accountPolkadotExtrinsicRelationTable,
  accountTable,
  actionSourceTable,
  actionTable,
  lockedPeriodActionTable,
  polkadotExtrinsicTable,
  portfolioTable,
} from '../../schema'
import type { DatabaseWithTransaction } from '../../types'
import { conflictUpdateAllExcept, getMaxInsertRowCount } from '../../utils'
import { applyActionEditRulesToInsertedActionsOnDatabase } from './applyActionEditRulesToInsertedActions'
import { deleteAssistantProposalsForSourceIds } from './deleteAssistantProposalsForActionKeys'
import {
  applyRulesToLockedInsertRowsInTransaction,
  createAssetInsertQueries,
  createLockedActionInsertQueries,
  createOpenActionInsertQueries,
  filterActionAccountRelations,
  getClosedAccountingPeriodBoundary,
  partitionSourceIdsByFiscalLock,
  prepareFiscalLockInsertRows,
} from './fiscalLock'

type SavePolkadotActionsParameters = {
  database: DatabaseWithTransaction
  chainId: number
  blocks: readonly {
    blockNumber: number
    extrinsicIndexes: (number | undefined)[]
  }[]
  actions: readonly Action[]
}

/** Polkadot block data から生成したデータを保存
 * - portfolio.isTxGenerated を false に更新
 * - block に関連する accountPolkadotExtrinsicRelation.analyzed を true に更新
 * - block > extrinsic を actionSource に保存 (upsert)
 * - actionSource に関連する既存データ (action & cascade 要素) を削除
 * - action 関連データを保存
 *   - crypto currency
 *   - nft
 *   - action
 *   - action transfer
 *   - action account relation
 * - account.syncing を更新
 */
export const savePolkadotActions = async ({
  database: { database, transaction },
  chainId,
  blocks,
  actions,
}: SavePolkadotActionsParameters) => {
  if (blocks.length === 0 && actions.length === 0) {
    return
  }

  const blockNumbers: number[] = []
  const actionSourceInsertRows: {
    id: string
    type: 'polkadot'
    polkadotChainId: number
    polkadotBlockNumber: number
    polkadotExtrinsicIndex?: number
  }[] = []
  for (const block of blocks) {
    blockNumbers.push(block.blockNumber)
    for (const extrinsicIndex of block.extrinsicIndexes) {
      const id = createPolkadotSource({
        chainId,
        blockNumber: block.blockNumber,
        extrinsicIndex,
      })
      actionSourceInsertRows.push({
        id,
        type: 'polkadot',
        polkadotChainId: chainId,
        polkadotBlockNumber: block.blockNumber,
        polkadotExtrinsicIndex: extrinsicIndex,
      })
    }
  }
  const closedBoundary = await getClosedAccountingPeriodBoundary({
    database: { database, transaction },
  })
  const blockTimestampRows =
    blockNumbers.length === 0
      ? []
      : await database
          .select({
            blockNumber: polkadotExtrinsicTable.blockNumber,
            timestamp: polkadotExtrinsicTable.timestamp,
          })
          .from(polkadotExtrinsicTable)
          .where(
            and(
              eq(polkadotExtrinsicTable.chainId, chainId),
              inArray(polkadotExtrinsicTable.blockNumber, blockNumbers),
            ),
          )
  const blockTimestampMap = new Map<number, number>()
  for (const row of blockTimestampRows) {
    if (!blockTimestampMap.has(row.blockNumber)) {
      blockTimestampMap.set(row.blockNumber, row.timestamp.getTime())
    }
  }
  const { openSourceIds, lockedSourceIds, unclassifiedSourceIds } =
    partitionSourceIdsByFiscalLock({
      sourceRows: actionSourceInsertRows.map((row) => ({
        sourceId: row.id,
        timestamp: blockTimestampMap.get(row.polkadotBlockNumber),
      })),
      closedBoundary,
    })
  const deletingOpenSourceIds = [
    ...new Set([...openSourceIds, ...unclassifiedSourceIds]),
  ]
  const deletingLockedSourceIds = [
    ...new Set([...lockedSourceIds, ...unclassifiedSourceIds]),
  ]

  const accountRows = await database
    .select({ id: accountTable.id, address: accountTable.polkadotAddress })
    .from(accountTable)
    .where(eq(accountTable.polkadotChainId, chainId))
  const accountIds: number[] = []
  const accounts: { id: number; address: string }[] = []
  for (const row of accountRows) {
    accountIds.push(row.id)
    if (row.address === null) {
      continue
    }
    accounts.push({ id: row.id, address: row.address })
  }
  const actionAccountRelations = generateActionAccountRelations({
    chainId,
    actions,
    accounts,
  })

  const {
    openActionIds: insertedActionIdList,
    openInsertRows,
    lockedInsertRows,
  } = await prepareFiscalLockInsertRows({
    database: { database, transaction },
    actions,
    createLockedActionAccountRelations: ({ lockedActions }) =>
      filterActionAccountRelations({
        actionAccountRelations,
        actionIds: lockedActions.map(
          (action) => `${action.source}_${action.order}`,
        ),
      }),
  })
  const shouldInvalidateCalculation =
    deletingOpenSourceIds.length > 0 || insertedActionIdList.length > 0

  await transaction([
    (tx) => [
      deleteAssistantProposalsForSourceIds({
        database: tx,
        sourceIds: deletingOpenSourceIds,
      }),
    ],
    (tx) => [
      ...(shouldInvalidateCalculation
        ? [tx.update(portfolioTable).set({ isTxGenerated: false })]
        : []),
      // block に関連する accountPolkadotExtrinsicRelation.analyzed を true に更新
      ...divideArrayIntoChunks(
        blockNumbers,
        sqliteMaxHostParameterCount - accountIds.length - 2,
      ).map((chunk) =>
        tx
          .update(accountPolkadotExtrinsicRelationTable)
          .set({ analyzed: true })
          .where(
            and(
              inArray(
                accountPolkadotExtrinsicRelationTable.accountId,
                accountIds,
              ),
              inArray(accountPolkadotExtrinsicRelationTable.blockNumber, [
                ...chunk,
              ]),
            ),
          ),
      ),
      // block > extrinsic を actionSource に保存 (upsert)
      ...divideArrayIntoChunks(
        actionSourceInsertRows,
        getMaxInsertRowCount(actionSourceColumnCount, true),
      ).map((chunk) =>
        tx
          .insert(actionSourceTable)
          .values([...chunk])
          .onConflictDoUpdate({
            target: [actionSourceTable.id],
            set: conflictUpdateAllExcept(actionSourceTable, ['id']),
          }),
      ),
      // actionSource に関連する action 削除
      ...divideArrayIntoChunks(
        deletingOpenSourceIds,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx.delete(actionTable).where(inArray(actionTable.sourceId, [...chunk])),
      ),
      ...divideArrayIntoChunks(
        deletingLockedSourceIds,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx
          .delete(lockedPeriodActionTable)
          .where(inArray(lockedPeriodActionTable.sourceId, [...chunk])),
      ),
    ],
    (tx) => [
      ...createAssetInsertQueries({ tx, openInsertRows, lockedInsertRows }),
    ],
    (tx) => [
      (async () => {
        const preparedLockedInsertRows =
          await applyRulesToLockedInsertRowsInTransaction({
            database: tx,
            lockedInsertRows,
          })

        await Promise.all([
          ...createOpenActionInsertQueries({
            tx,
            openInsertRows,
            actionAccountRelationRows: filterActionAccountRelations({
              actionAccountRelations,
              actionIds: insertedActionIdList,
            }),
          }),
          ...accountIds.map((id) =>
            tx
              .update(accountTable)
              .set({
                syncing: sql`(EXISTS (SELECT 1 FROM ${accountPolkadotExtrinsicRelationTable} WHERE ${accountPolkadotExtrinsicRelationTable.accountId} = ${id} AND ${accountPolkadotExtrinsicRelationTable.analyzed} = ${false}))`,
              })
              .where(eq(accountTable.id, id)),
          ),
          ...createLockedActionInsertQueries({
            tx,
            lockedInsertRows: preparedLockedInsertRows,
          }),
        ])

        if (insertedActionIdList.length > 0) {
          await applyActionEditRulesToInsertedActionsOnDatabase({
            database: tx,
            actionIdList: insertedActionIdList,
          })
        }
      })(),
    ],
  ])
}

/** action から actionAccountRelation data を作成
 * - action transfer の from, to に account address が含まれていれば account に関連づける
 */
const generateActionAccountRelations = (params: {
  chainId: number
  actions: readonly Action[]
  accounts: readonly { id: number; address: string }[]
}): readonly { actionId: string; accountId: number }[] => {
  const { chainId, actions, accounts } = params

  const polkadotAddressIdMap = new Map<string, number>(
    accounts.map(({ address, id }) => [
      createPolkadotAddress({ chainId, address }),
      id,
    ]),
  )

  const relationMap = new Map<string, { actionId: string; accountId: number }>()
  for (const action of actions) {
    const actionId = `${action.source}_${action.order}`

    // action transfer の from, to と一致する account を追加
    for (const transfer of action.transfers) {
      const transferFromAccountId =
        transfer.from === undefined
          ? undefined
          : polkadotAddressIdMap.get(transfer.from)
      if (transferFromAccountId !== undefined) {
        const id = `${actionId}/${transferFromAccountId}`
        if (!relationMap.has(id)) {
          relationMap.set(actionId, {
            actionId,
            accountId: transferFromAccountId,
          })
        }
      }
      const transferToAccountId =
        transfer.to === undefined
          ? undefined
          : polkadotAddressIdMap.get(transfer.to)
      if (transferToAccountId !== undefined) {
        const id = `${actionId}/${transferToAccountId}`
        if (!relationMap.has(id)) {
          relationMap.set(actionId, {
            actionId,
            accountId: transferToAccountId,
          })
        }
      }
    }
  }

  return [...relationMap.values()]
}
